const IntegrationSupport = () => {
  return (
    <>
      <p>
        <em>
          The psychedelic experience is not the therapy. Integration &mdash; the
          sustained process of making meaning from altered-state material and
          translating it into lasting psychological change &mdash; is where
          clinical outcomes are determined. It is also the phase that generates
          the most complex, multi-layered documentation challenges in
          psychedelic-assisted therapy. This guide explores what integration
          documentation requires, why standard note formats often fall short,
          and how to build a clinical record that captures the richness of this
          work.
        </em>
      </p>

      <hr />

      <h2>Why Integration Is Where the Real Work Happens</h2>

      <p>
        There is a common misconception &mdash; among the public and sometimes
        among clinicians new to psychedelic-assisted therapy (PAT) &mdash; that
        the dosing session is the treatment. The logic seems intuitive: the
        psychedelic experience produces the insights, so the experience itself
        must be the active ingredient. But decades of clinical work and an
        expanding evidence base tell a different story.
      </p>

      <p>
        Stanislav Grof, one of the earliest clinical researchers in this field,
        emphasised that psychedelic experiences are amplifiers of psychological
        material, not cures in themselves. Rosalind Watts and colleagues at
        Imperial College London have written extensively about the
        &ldquo;afterglow&rdquo; period following psilocybin sessions, noting
        that initial improvements in mood and connectedness often fade without
        adequate integration support. Bill Richards, drawing on decades of work
        at Johns Hopkins, has described integration as the process by which
        &ldquo;mystical&rdquo; or peak experiences become personally meaningful
        and psychologically transformative rather than simply memorable.
      </p>

      <p>
        The emerging consensus is clear: the dosing session opens a window. It
        may surface repressed material, dissolve habitual defences, generate
        powerful emotional or somatic experiences, or produce states of profound
        connectedness and meaning. But whether those experiences lead to durable
        therapeutic change depends almost entirely on what happens next. A
        client who has a deeply moving experience of self-compassion during a
        psilocybin session but returns to the same rigid thought patterns,
        avoidant behaviours, and unsupportive environment within weeks has not
        been treated. They have had an experience. Integration is what turns
        experience into treatment.
      </p>

      <p>
        This has direct implications for documentation. If integration is where
        clinical outcomes are shaped, then integration documentation is not a
        secondary administrative task. It is a core clinical record &mdash; one
        that needs to capture material far more complex than what a standard
        progress note was designed to hold.
      </p>

      <h2>The Documentation Challenge of Integration</h2>

      <p>
        A typical integration session might involve a client revisiting a vivid
        image from the dosing experience, connecting it to an early childhood
        memory, experiencing a wave of grief that manifests as chest tightness,
        articulating a new understanding of a relational pattern, and making a
        behavioural commitment grounded in a value they only recently
        recognised. That is one session. And the clinician needs to document all
        of it in a way that is clinically meaningful, legally defensible,
        connected to the treatment plan, and useful for future sessions.
      </p>

      <p>
        Standard progress note formats &mdash; whether SOAP, BIRP, or DAP &mdash;
        were designed for conventional therapy sessions where the intervention
        is relatively discrete and the content relatively focused. Integration
        sessions in PAT rarely fit that mould. The material is multi-layered,
        often non-linear, and spans cognitive, emotional, somatic, relational,
        and sometimes spiritual domains simultaneously. A note that captures
        only the behavioural plan misses the meaning-making. A note that
        captures only the emotional processing misses the clinical reasoning. A
        note that tries to capture everything in free-text narrative becomes
        unwieldy and difficult to reference later.
      </p>

      <p>
        The challenge is compounded by the fact that integration is not a single
        session. It is an ongoing process that may span weeks or months,
        with material from the dosing experience continuing to unfold, shift,
        and deepen across multiple contacts. Documentation needs to track this
        evolution &mdash; not just what emerged, but how it changed, what was
        consolidated, what was revisited, and what was ultimately integrated
        into the client's sense of self and daily functioning.
      </p>

      <p>
        For clinicians already managing the documentation burden of a full
        caseload, integration notes can feel particularly demanding. The irony
        is that this is precisely the phase where thorough documentation matters
        most &mdash; for clinical continuity, for supervision, for research
        contributions, and for the client's own reflective process.
      </p>

      <h2>Key Documentation Domains in Integration</h2>

      <p>
        Rather than forcing integration material into a single note format,
        effective documentation in this phase tends to draw on several
        overlapping domains. These can be structured as sections within a
        session note, as separate linked documents, or as components of a
        purpose-built integration template.
      </p>

      <h3>Adapted Progress Notes</h3>

      <p>
        The familiar SOAP structure can be adapted for integration sessions,
        though each element takes on a distinct character in this context.
      </p>

      <ul>
        <li>
          <strong>Subjective.</strong> In integration, the subjective section
          captures the client's account of their ongoing experience since the
          dosing session: what has surfaced, what has shifted, what feels
          unresolved, and how they are making sense of the material. This often
          includes dream content, spontaneous memories, changes in mood or
          perception, and shifts in relational dynamics. Unlike a standard
          therapy session where the subjective is typically
          &ldquo;how the week went,&rdquo; here it is a report on an unfolding
          psycho-spiritual process.
        </li>
        <li>
          <strong>Objective.</strong> Observable presentation during the
          integration session: affect, somatic markers, level of engagement,
          capacity for reflection, and any signs of destabilisation or
          dysregulation. In PAT, the objective section should also note the
          client's capacity to oscillate between experiential processing and
          reflective meaning-making &mdash; a key indicator of integration
          readiness.
        </li>
        <li>
          <strong>Assessment.</strong> Clinical reasoning linking the
          integration material to the treatment formulation and goals. This is
          where the clinician documents their understanding of how the
          psychedelic material relates to the presenting problems, what
          therapeutic processes are at work, and where the client sits on the
          trajectory toward their treatment goals. This section should
          explicitly reference the treatment plan and
          formulation.
        </li>
        <li>
          <strong>Plan.</strong> Next steps for integration: specific practices
          (journaling, meditation, somatic exercises), relational experiments,
          topics to explore in subsequent sessions, and any decisions about the
          timing of future dosing sessions. The plan should also address safety
          and stabilisation if the client is navigating difficult material.
        </li>
      </ul>

      <h3>Meaning-Making Documentation</h3>

      <p>
        One of the most distinctive aspects of integration documentation is
        the need to capture the client's evolving narrative &mdash; the story
        they are constructing from the psychedelic experience and its
        relationship to their life history. This domain includes:
      </p>

      <ul>
        <li>
          <strong>Key images and memories.</strong> The specific visual,
          somatic, or emotional content from the dosing session that the client
          returns to during integration. These anchoring experiences often
          carry the most therapeutic significance and may be revisited across
          multiple sessions.
        </li>
        <li>
          <strong>Emerging narratives.</strong> How the client is making sense
          of what they experienced. What meanings are they constructing? How
          are those meanings evolving across sessions? Where are the narratives
          coherent, and where do contradictions or tensions remain?
        </li>
        <li>
          <strong>Life history connections.</strong> Links the client draws
          between psychedelic material and their personal history &mdash;
          childhood experiences, relational patterns, critical life events,
          losses, and formative moments. These connections are often where the
          deepest integration occurs.
        </li>
        <li>
          <strong>Values and commitments.</strong> Psychedelic experiences
          frequently clarify or reorder personal values. Documenting what the
          client identifies as important &mdash; and what concrete commitments
          they make based on that clarity &mdash; creates an accountability
          record that can be revisited in later sessions.
        </li>
      </ul>

      <h3>Trauma-Informed Processing Notes</h3>

      <p>
        Many clients in PAT are working with trauma material, and psychedelic
        sessions frequently surface traumatic memories, affects, and somatic
        states. Integration documentation for trauma work requires particular
        care:
      </p>

      <ul>
        <li>
          <strong>Trauma themes.</strong> What traumatic material has emerged
          and how it connects to the client's broader trauma history. This
          should be documented with sufficient clinical specificity for
          treatment planning while maintaining appropriate boundaries around
          level of detail.
        </li>
        <li>
          <strong>Emotional processing.</strong> The trajectory of emotional
          processing across sessions: what emotions have been accessed, what
          has been expressed and witnessed, what remains defended or
          dissociated, and what shifts in emotional capacity are evident.
        </li>
        <li>
          <strong>Cognitive and narrative shifts.</strong> Changes in
          trauma-related cognitions and self-narratives. Has the client moved
          from self-blame to self-compassion? From a fragmented account to a
          more coherent narrative? From avoidance to approach? These shifts are
          core outcomes in trauma therapy and deserve careful tracking.
        </li>
        <li>
          <strong>Somatic processing.</strong> Body-based experiences during
          integration: areas of tension or release, movements or gestures that
          carry meaning, changes in the client's relationship to physical
          sensations associated with the trauma. Somatic processing is often
          central to psychedelic-assisted trauma work and is easily lost in
          purely cognitive documentation formats.
        </li>
        <li>
          <strong>Safety and stabilisation.</strong> Ongoing assessment of the
          client's capacity to manage the material that has surfaced. This
          includes sleep, grounding practices, social support, substance use,
          and any signs of destabilisation. Integration can be destabilising,
          and documenting the clinician's active monitoring of safety is both
          clinically essential and medico-legally important.
        </li>
      </ul>

      <h3>Therapeutic Insight Tracking</h3>

      <p>
        Integration unfolds over time, and individual insights from a single
        session may only become clinically significant when they are tracked
        across the broader arc of treatment. Effective documentation captures:
      </p>

      <ul>
        <li>
          <strong>Insight emergence.</strong> What specific realisations or
          understandings arose in each session, recorded in the client's own
          language where possible.
        </li>
        <li>
          <strong>Insight development.</strong> How those insights evolve,
          deepen, or are challenged across subsequent sessions. An insight that
          feels transformative in session three may be nuanced, revised, or
          consolidated by session eight.
        </li>
        <li>
          <strong>Behavioural translation.</strong> Whether and how insights
          translate into changed behaviour, relational patterns, or daily
          functioning. This is the bridge between integration and outcome
          &mdash; the point where experiential learning becomes observable
          change.
        </li>
        <li>
          <strong>Outcome linkage.</strong> Explicit connections between tracked
          insights and treatment goals, outcome measures, and functional
          improvements. This linkage is what makes the clinical record
          coherent and audit-ready.
        </li>
      </ul>

      <h2>How Modality Shapes Integration Documentation</h2>

      <p>
        Psychedelic-assisted therapy is not a single modality. It is a
        framework that can be combined with a range of therapeutic orientations,
        and the clinician's theoretical lens significantly shapes what is
        emphasised during integration and, consequently, what needs to be
        documented. Understanding this relationship between therapeutic modality and
        documentation focus is essential for building records that are both
        clinically useful and theoretically coherent.
      </p>

      <h3>Internal Family Systems (IFS)</h3>

      <p>
        IFS-informed integration focuses on the client's internal system of
        parts. Documentation in this framework centres on which parts emerged
        during the psychedelic experience, what their roles and burdens are, how
        they relate to each other and to the Self, and what unburdening
        processes occurred or are in progress. Integration notes should track
        the development of Self-leadership &mdash; the client's growing
        capacity to relate to their parts from a place of curiosity, compassion,
        and calm rather than reactivity. Key documentation elements include
        parts maps, polarisation dynamics, protector-exile relationships, and
        the sequence of unburdening work.
      </p>

      <h3>Somatic Approaches</h3>

      <p>
        Somatic-oriented integration emphasises the body as the primary site
        of therapeutic processing. Documentation focuses on body-based
        experiences during and after the dosing session: areas of holding,
        patterns of tension and release, autonomic regulation, and the
        resolution of freeze, fight, or flight responses. Integration notes
        in this framework should capture the client's evolving relationship
        with physical sensation, their capacity for interoception, and any
        somatic completion sequences that emerge. Tracking shifts in the
        client's window of tolerance and autonomic flexibility is particularly
        relevant.
      </p>

      <h3>Transpersonal and Existential Approaches</h3>

      <p>
        Transpersonal integration foregrounds the spiritual, existential, and
        numinous dimensions of the psychedelic experience. Documentation in
        this framework captures experiences of unity, transcendence,
        interconnection, encounter with the sacred, and confrontation with
        existential themes such as mortality, meaning, and isolation.
        Integration notes should track how these experiences are being
        incorporated into the client's worldview, spiritual practice, and sense
        of purpose. This is the domain where documentation most clearly
        diverges from conventional clinical note-writing, and clinicians may
        need to develop language and frameworks that honour the client's
        experience while maintaining clinical rigour.
      </p>

      <h3>Acceptance and Commitment Therapy (ACT)</h3>

      <p>
        ACT-informed integration emphasises psychological flexibility: the
        client's capacity to be present with difficult internal experiences,
        defuse from unhelpful thought patterns, connect with values, and take
        committed action. Documentation focuses on shifts in experiential
        avoidance, changes in cognitive fusion, values clarification that
        emerged during the psychedelic experience, and specific committed
        actions the client has identified. Integration notes should track the
        client's movement across the ACT hexaflex &mdash; present-moment
        awareness, acceptance, defusion, self-as-context, values, and
        committed action &mdash; and connect psychedelic-session material to
        each of these processes.
      </p>

      <h3>Psychodynamic Approaches</h3>

      <p>
        Psychodynamic integration centres on unconscious material, attachment
        patterns, and the therapeutic relationship itself. Documentation
        captures the symbolic content of the psychedelic experience,
        connections to early relational templates, transference dynamics that
        emerged during the dosing session or integration, and the evolution of
        the client's relationship to unconscious material. Integration notes
        in this framework should attend to defences &mdash; what was loosened
        during the psychedelic experience, what has reconsolidated, and how
        the client is relating to previously unconscious material now that it
        is more accessible. The therapeutic relationship is a key site of
        integration and should be documented as such.
      </p>

      <h2>Document Chaining Through Integration</h2>

      <p>
        Integration documentation does not exist in isolation. It is part of a document chain that begins with
        preparation and screening, runs through the dosing session, and extends
        through integration into treatment plan reviews, outcome assessment,
        and eventually discharge. Understanding how integration notes connect
        to the documents that precede and follow them is essential for building
        a coherent clinical record.
      </p>

      <h3>Drawing From Dosing Session Observations</h3>

      <p>
        The dosing session record provides the raw observational data that
        integration notes interpret and develop. During the dosing session, the
        clinician (or sitter) typically documents observable behaviour, verbal
        utterances, emotional expressions, somatic movements, and the arc of
        the experience over time. This record is primarily descriptive. It
        captures what happened.
      </p>

      <p>
        Integration notes build on this material by adding the client's
        subjective account, the clinician's formulation, and the meaning-making
        that occurs in the days and weeks following the experience. A thorough
        integration note will explicitly reference specific moments from the
        dosing session &mdash; &ldquo;the client revisited the image of the
        tree that first appeared at approximately two hours into the dosing
        session&rdquo; &mdash; creating a clear thread between observation and
        interpretation.
      </p>

      <h3>Feeding Forward Into Reviews and Planning</h3>

      <p>
        Integration notes are also the primary source material for treatment
        plan reviews in PAT. As the integration process unfolds across
        multiple sessions, patterns emerge: certain themes consolidate, certain
        insights translate into behavioural change, certain areas remain
        stuck. The treatment plan review synthesises this trajectory and makes
        recommendations about next steps &mdash; whether that is continued
        integration work, additional dosing sessions, a shift in therapeutic
        focus, or preparation for discharge.
      </p>

      <p>
        When integration notes are well-structured and consistently linked to
        treatment goals, writing these reviews becomes substantially easier.
        The clinician is not reconstructing the narrative from memory or
        scanning through pages of undifferentiated free text. They are drawing
        on a structured record that already tracks insights, behavioural
        changes, trauma processing, and outcome linkages.
      </p>

      <p>
        Similarly, the discharge summary for a PAT episode draws heavily on
        integration documentation. It needs to articulate what psychedelic
        material emerged, how it was integrated, what changes resulted, what
        remains unresolved, and what ongoing support the client may need. A
        well-maintained chain of integration notes makes this summary a
        synthesis rather than a reconstruction.
      </p>

      <h2>Common Documentation Pitfalls in Integration</h2>

      <p>
        Even experienced clinicians can fall into patterns that undermine the
        quality and utility of their integration documentation. Several
        pitfalls are worth naming.
      </p>

      <p>
        <strong>Over-documenting experiential content, under-documenting
        clinical reasoning.</strong> It is tempting to capture the vivid imagery
        and emotional intensity of psychedelic material in rich detail while
        neglecting the clinical assessment that makes the note meaningful.
        A good clinical note balances
        the client's experience with the clinician's formulation. The question
        is not just &ldquo;what did the client experience?&rdquo; but
        &ldquo;what does the clinician understand about what this experience
        means for treatment?&rdquo;
      </p>

      <p>
        <strong>Failing to link integration material to treatment goals.</strong>
        {" "}Integration can feel open-ended and exploratory, and it should be.
        But the clinical record still needs to connect this exploration to the
        agreed treatment plan. If a client spends three sessions processing a
        childhood memory that surfaced during the dosing experience, the notes
        should make clear how this processing relates to the presenting
        problems, the formulation, and the treatment goals. This is not about
        forcing a reductive framework onto rich material. It is about
        maintaining clinical coherence.
      </p>

      <p>
        <strong>Inconsistent tracking across sessions.</strong> Integration is
        a longitudinal process, and its documentation value depends on
        continuity. If insights are noted in session four but not referenced
        in sessions five through seven, only to reappear in session eight, the
        record does not capture the arc of integration. Consistent use of
        structured templates &mdash; where insight tracking, trauma processing,
        and meaning-making are revisited each session &mdash; prevents this
        fragmentation.
      </p>

      <p>
        <strong>Neglecting safety documentation.</strong> Integration can be
        destabilising. Clients may experience emotional flooding, dissociative
        episodes, sleep disruption, or existential distress as psychedelic
        material continues to unfold. Documenting the clinician's ongoing
        assessment of safety, stabilisation strategies, and risk monitoring is
        not optional. It is a core component of competent PAT practice and a
        medico-legal necessity.
      </p>

      <h2>Practical Guidance: Building Your Integration Documentation System</h2>

      <p>
        Given the complexity of integration documentation, clinicians benefit
        from having structured templates and consistent workflows rather than
        approaching each note from scratch. Several principles are worth
        adopting.
      </p>

      <p>
        <strong>Use purpose-built templates.</strong> Generic progress note
        templates were not designed for integration work. Templates that include
        dedicated sections for meaning-making, somatic processing, insight
        tracking, and safety assessment will capture richer material with less
        effort. MycenAI's integration templates are designed with these domains
        in mind, providing structured prompts that guide thorough documentation
        without prescribing content.
      </p>

      <p>
        <strong>Adopt a consistent structure across sessions.</strong> When
        every integration note follows the same structure, longitudinal
        tracking becomes straightforward. You can scan across sessions to see
        how specific themes have evolved, which insights have translated into
        behaviour, and where processing remains incomplete. Consistency also
        makes it easier for supervisors, peer reviewers, or future treating
        clinicians to navigate the record.
      </p>

      <p>
        <strong>Link every note to the document chain.</strong> Each
        integration note should reference the dosing session record it draws
        from and the treatment plan it connects to. This document chaining practice
        ensures the clinical record reads as a coherent narrative rather than a
        collection of standalone entries. When it comes time to write a
        treatment plan review or discharge summary, the chain is already
        built.
      </p>

      <p>
        <strong>Balance detail with efficiency.</strong> Thorough documentation
        does not mean exhaustive documentation. The goal is to capture what is
        clinically significant &mdash; the material that matters for
        formulation, treatment planning, outcome tracking, and continuity of
        care &mdash; without transcribing every moment of every session. This
        is where structured templates and AI-assisted documentation tools
        become particularly valuable: they help clinicians capture the right
        level of detail efficiently, reducing the administrative burden without
        sacrificing clinical quality.
      </p>

      <p>
        <strong>Use the Clinical Prompt Library.</strong> MycenAI's
        downloadable Clinical Prompt Library includes integration-specific
        prompts designed to support documentation across the domains described
        in this article. These prompts are modality-aware, meaning they can be
        tailored to IFS, somatic, ACT, psychodynamic, or transpersonal
        frameworks depending on the clinician's orientation and the client's
        needs.
      </p>

      <h2>Integration Documentation as Clinical Practice</h2>

      <p>
        There is a final point worth making. In psychedelic-assisted therapy,
        the act of documenting integration is not merely administrative. It is
        a form of clinical practice in itself. The process of articulating what
        emerged during the dosing experience, how the client is making meaning,
        what therapeutic processes are at work, and where the integration is
        heading forces the clinician to do their own integration &mdash; to
        synthesise their observations, sharpen their formulation, and think
        clearly about treatment direction.
      </p>

      <p>
        Well-structured integration documentation serves the clinician as much
        as it serves the record. It supports reflective practice, enhances
        supervision, contributes to the growing evidence base for PAT, and
        ultimately improves client outcomes. It is work worth doing well.
      </p>

      <p>
        The field of psychedelic-assisted therapy is still developing its
        documentation standards. Clinicians who establish rigorous, structured
        integration documentation practices now are not only protecting
        themselves and their clients &mdash; they are helping to set the
        standard for the profession as it matures.
      </p>

      <hr />

      <p>
        <em>
          MycenAI provides integration documentation templates and a
          downloadable Clinical Prompt Library designed for psychedelic-assisted
          therapy clinicians. Built on hardware-secured confidential computing,
          with no clinical content stored and no data used for model training.
          Learn more at{" "}
          <a href="https://confideai.ai/welcome/mycenai">confideai.ai</a>.
        </em>
      </p>
    </>
  );
};

export default IntegrationSupport;
